import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Plus, X } from "lucide-react";

const BLANK_WEDGE = { label: "", color: "#D4AF37", weight: 1, resolution: "narrated", auto_award_points: 0, auto_damage_mode: "none", auto_damage_dice: "", auto_damage_target: "landing", auto_skip_turn: false, narrated_prompt: "" };

export function GameShowWheelEditor({ show }) {
  const [wedges, setWedges] = useState(show.wheel?.wedges || []);

  useEffect(() => setWedges(show.wheel?.wedges || []), [show.id]);

  const update = (idx, patch) => setWedges((w) => w.map((x, i) => (i === idx ? { ...x, ...patch } : x)));
  const addWedge = () => setWedges((w) => [...w, { ...BLANK_WEDGE, id: undefined }]);
  const removeWedge = (idx) => setWedges((w) => w.filter((_, i) => i !== idx));

  const save = async () => {
    if (wedges.some((w) => !w.label.trim())) return toast.error("Every wedge needs a label");
    try {
      await api.patch(`/game-shows/${show.id}/wheel`, { wedges });
      toast.success("Wheel saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save the wheel");
    }
  };

  return (
    <div className="space-y-3 border-t border-white/10 pt-4" data-testid="gameshow-wheel-editor">
      <p className="text-sm uppercase tracking-widest text-[#f1e0c5]/40">Wheel Wedges</p>
      {wedges.map((w, idx) => (
        <div key={idx} data-testid={`gameshow-wedge-${idx}`} className="bg-black/20 rounded-xl p-3 space-y-2">
          <div className="flex items-center gap-2">
            <input type="color" value={w.color} onChange={(e) => update(idx, { color: e.target.value })} className="w-8 h-8 rounded border-none bg-transparent" data-testid={`gameshow-wedge-color-${idx}`} />
            <Input data-testid={`gameshow-wedge-label-${idx}`} placeholder="Wedge label (e.g. 500 Points)" value={w.label} onChange={(e) => update(idx, { label: e.target.value })} className="bg-black/30 border-white/10 flex-1" />
            <Input data-testid={`gameshow-wedge-weight-${idx}`} type="number" min={1} value={w.weight} onChange={(e) => update(idx, { weight: parseInt(e.target.value, 10) || 1 })} className="bg-black/30 border-white/10 w-16" title="Weight (odds)" />
            <button data-testid={`gameshow-wedge-remove-${idx}`} onClick={() => removeWedge(idx)} className="text-[#f1e0c5]/30 hover:text-[#D90429]"><X className="w-4 h-4" /></button>
          </div>
          <Select value={w.resolution} onValueChange={(v) => update(idx, { resolution: v })}>
            <SelectTrigger data-testid={`gameshow-wedge-resolution-${idx}`} className="bg-black/30 border-white/10 h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="narrated">GM-Narrated (you decide what happens)</SelectItem>
              <SelectItem value="auto">Auto-Resolve (points/damage applied instantly)</SelectItem>
            </SelectContent>
          </Select>
          {w.resolution === "narrated" ? (
            <Textarea data-testid={`gameshow-wedge-prompt-${idx}`} placeholder="What happens when this is spun? (shown to you as a reminder)" value={w.narrated_prompt || ""} onChange={(e) => update(idx, { narrated_prompt: e.target.value })} className="bg-black/30 border-white/10 text-xs h-16" />
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <label className="text-[10px] text-[#f1e0c5]/40">Award Points</label>
                <Input data-testid={`gameshow-wedge-points-${idx}`} type="number" value={w.auto_award_points} onChange={(e) => update(idx, { auto_award_points: parseInt(e.target.value, 10) || 0 })} className="bg-black/30 border-white/10 h-8 text-xs" />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] text-[#f1e0c5]/40">Damage Mode</label>
                <Select value={w.auto_damage_mode} onValueChange={(v) => update(idx, { auto_damage_mode: v })}>
                  <SelectTrigger data-testid={`gameshow-wedge-damage-mode-${idx}`} className="bg-black/30 border-white/10 h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Damage</SelectItem>
                    <SelectItem value="points">Damage (subtracts points)</SelectItem>
                    <SelectItem value="character_hp">Damage (real character HP)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {w.auto_damage_mode !== "none" && (
                <>
                  <div className="space-y-1">
                    <label className="text-[10px] text-[#f1e0c5]/40">Damage Dice</label>
                    <Input data-testid={`gameshow-wedge-damage-dice-${idx}`} placeholder="e.g. 2d6" value={w.auto_damage_dice || ""} onChange={(e) => update(idx, { auto_damage_dice: e.target.value })} className="bg-black/30 border-white/10 h-8 text-xs" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] text-[#f1e0c5]/40">Target</label>
                    <Select value={w.auto_damage_target} onValueChange={(v) => update(idx, { auto_damage_target: v })}>
                      <SelectTrigger data-testid={`gameshow-wedge-damage-target-${idx}`} className="bg-black/30 border-white/10 h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="landing">Just the Player who spun</SelectItem>
                        <SelectItem value="all">Everyone</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              <label className="flex items-center gap-2 col-span-2 cursor-pointer">
                <Checkbox data-testid={`gameshow-wedge-skip-turn-${idx}`} checked={w.auto_skip_turn} onCheckedChange={(v) => update(idx, { auto_skip_turn: !!v })} />
                <span className="text-[10px] text-[#f1e0c5]/50">Also: lose a turn</span>
              </label>
            </div>
          )}
        </div>
      ))}
      <div className="flex gap-2">
        <Button data-testid="gameshow-add-wedge-button" size="sm" variant="secondary" className="rounded-full gap-1.5" onClick={addWedge}><Plus className="w-3.5 h-3.5" /> Add Wedge</Button>
        <Button data-testid="gameshow-save-wheel-button" size="sm" className="rounded-full bg-[#1E3A8A] hover:bg-[#2b4fab]" onClick={save}>Save Wheel</Button>
      </div>
    </div>
  );
}
